<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's' || $screen === 'm'
      }
    ]"
  >
    <div :class="$style.left">
      <BaseVisualBanner
        v-if="!loadingTwoFactorSettings && !isTwoFactorEnabled"
        :heading="$t('dashboard.2fa-announcement.title')"
        :description="$t('dashboard.2fa-announcement.description')"
        :primaryAction="{
          text: $t('dashboard.2fa-announcement.link'),
          routerLink: {
            name: 'enable-two-factor-auth-settings'
          }
        }"
        color="green"
        size="s"
        imagePath="/img/admin/2fa.svg"
        v-test="'2fa-banner'"
      />
      <BaseHeading>{{ t('admin.dashboard.popular_settings') }}</BaseHeading>
      <router-link
        v-for="card in cards"
        :key="card.key"
        :to="{ name: card.link }"
      >
        <BaseCard v-test="`${card.key}-link`">
          <div :class="$style.cardContent">
            <img
              :src="card.image"
              :class="$style.image"
            >
            <div>
              <BaseHeading>{{ card.title }}</BaseHeading>
              <BaseText>{{ card.paragraph }}</BaseText>
            </div>
          </div>
        </BaseCard>
      </router-link>
    </div>
    <div :class="$style.right">
      <BaseCard
        :heading="t('admin.dashboard.your_stats')"
        :loading="loading"
      >
        <div
          v-if="result"
          :class="$style.stats"
        >
          <Flex
            direction="column"
            :gap="0.25"
          >
            <BaseHeading
              size="xl"
              center
              v-test="'total-customers'"
            >
              {{ localeString(result.companyStats.totalCustomers) }}
            </BaseHeading>
            <BaseText color="secondary">
              {{ t('admin.dashboard.stats.customers').toUpperCase() }}
            </BaseText>
          </Flex>
          <Flex
            direction="column"
            :gap="0.25"
          >
            <BaseHeading
              size="xl"
              center
              v-test="'total-invoices'"
            >
              {{ localeString(result.companyStats.totalInvoices) }}
            </BaseHeading>
            <BaseText color="secondary">
              {{ t(`admin.dashboard.stats.${orderKey('plural')}`).toUpperCase() }}
            </BaseText>
          </Flex>
          <Flex
            direction="column"
            :gap="0.25"
          >
            <BaseHeading
              size="xl"
              center
              v-test="'total-appointments'"
            >
              {{ localeString(result.companyStats.totalAppointments) }}
            </BaseHeading>
            <BaseText color="secondary">
              {{ t('admin.dashboard.stats.appointments').toUpperCase() }}
            </BaseText>
          </Flex>
        </div>
      </BaseCard>
      <SMSCard size="l" />
      <ReferralCard />
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { useI18n } from 'vue-i18n';
import { orderKey } from '@/helpers/compliant-register';
import { localeString } from '@/helpers/formatting';
import ReferralCard from '@/modules/_shared/ReferralCard.vue';
import SMSCard from '../_shared/SMSCard.vue';
import { getCompanyStats } from './graphql';

const { t } = useI18n();

const { result, loading } = useQuery(getCompanyStats);

const cards = [
  {
    key: 'services',
    link: 'admin-services',
    title: t('admin.dashboard.services.title'),
    paragraph: t('admin.dashboard.services.paragraph'),
    image: '/img/admin/dashboard/services.svg',
  },
  {
    key: 'timetable',
    link: 'admin-roster',
    title: t('admin.dashboard.timetable.title'),
    paragraph: t('admin.dashboard.timetable.paragraph'),
    image: '/img/admin/dashboard/timetable.svg',
  },
  {
    key: 'bookings',
    link: 'admin-online-bookings',
    title: t('admin.dashboard.bookings.title'),
    paragraph: t('admin.dashboard.bookings.paragraph'),
    image: '/img/admin/dashboard/online-bookings.svg',
  },
];

const { result: twoFactorSettings, loading: loadingTwoFactorSettings } =
  useQuery(
    gql`
      query getCurrentUser {
        currentUser {
          twoFactorSettings {
            status
          }
        }
      }
    `,
    null,
    {
      fetchPolicy: 'cache-first',
    },
  );

const isTwoFactorEnabled = computed(
  () =>
    twoFactorSettings.value?.currentUser.twoFactorSettings?.status ===
    'ENABLED',
);
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;

  &.smallScreen {
    flex-direction: column;
  }
}

.left {
  .base:not(.smallScreen) & {
    width: 60%;
  }
}

.left,
.right {
  flex-grow: 1;
  padding: $spacing;
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

.cardContent {
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  gap: $spacing;
}

.image {
  max-height: 128px;
  max-width: 128px;
}

.stats {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $spacing * 0.5;
}
</style>
